import React, { useEffect, useState, useCallback } from 'react';
import { ocorrenciaService } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';

/**
 * #63 Pilar C — Tags estruturadas da Ocorrência (Fase C frontend).
 *
 * Consome:
 *   - Fase A (#58): listar / adicionar manual / remover
 *   - Fase B (#62): "Sugerir com IA" (sync, ~5-15s) → aceitar/descartar
 *
 * Tags aceitas da IA persistem com fonte='ia_sugerida_confirmada'
 * (preserva proveniência). Quando a ocorrência está promovida (locked),
 * a UI fica read-only.
 */

// Espelha TIPOS_VALIDOS do backend (tag_service.py).
const TIPOS = [
  { value: 'processo', label: 'Processo' },
  { value: 'equipamento', label: 'Equipamento' },
  { value: 'turno', label: 'Turno' },
  { value: 'area', label: 'Área' },
  { value: 'produto', label: 'Produto' },
  { value: 'lote', label: 'Lote' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'fornecedor', label: 'Fornecedor' },
];

const TIPO_LABEL = Object.fromEntries(TIPOS.map((t) => [t.value, t.label]));

// Badge visual por fonte — ajuda o usuário entender de onde veio a tag.
// #213: ícone = nome de Material Symbol (design system), não emoji.
const FONTE_INFO = {
  manual: { icon: 'edit', titulo: 'Adicionada manualmente' },
  campo_espelho: { icon: 'link', titulo: 'Espelho de campo (Produto/Lote/Cliente)' },
  ia_sugerida_confirmada: { icon: 'smart_toy', titulo: 'Sugerida pela IA e confirmada' },
  herdada_ocorrencia: { icon: 'subdirectory_arrow_right', titulo: 'Herdada da ocorrência' },
};

function OcorrenciaTags({ ocId, locked = false }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form de adição manual
  const [novoTipo, setNovoTipo] = useState('processo');
  const [novoNome, setNovoNome] = useState('');
  const [adicionando, setAdicionando] = useState(false);

  // Sugestões da IA
  const [sugerindo, setSugerindo] = useState(false);
  const [sugestoes, setSugestoes] = useState(null); // null = não pediu ainda; [] = pediu e veio vazio
  const [aceitando, setAceitando] = useState(null); // chave da sugestão sendo aceita
  const [confirmandoTodas, setConfirmandoTodas] = useState(false); // #208 — aceite em lote

  const carregarTags = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ocorrenciaService.listarTags(ocId);
      setTags(data);
    } catch (err) {
      showError(`Erro ao carregar tags: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [ocId]);

  useEffect(() => {
    carregarTags();
  }, [carregarTags]);

  const handleAdicionarManual = async (e) => {
    e.preventDefault();
    if (!novoNome.trim() || adicionando) return;
    setAdicionando(true);
    try {
      await ocorrenciaService.adicionarTag(ocId, {
        tipo: novoTipo,
        nome: novoNome.trim(),
        fonte: 'manual',
      });
      setNovoNome('');
      showSuccess('Tag adicionada.');
      await carregarTags();
    } catch (err) {
      showError(`Erro ao adicionar tag: ${err.response?.data?.detail || err.message}`);
    } finally {
      setAdicionando(false);
    }
  };

  const handleRemover = async (occurrenceTagId) => {
    try {
      await ocorrenciaService.removerTag(ocId, occurrenceTagId);
      await carregarTags();
    } catch (err) {
      showError(`Erro ao remover tag: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSugerir = async () => {
    if (sugerindo) return;
    setSugerindo(true);
    setSugestoes(null);
    try {
      const data = await ocorrenciaService.sugerirTags(ocId);
      setSugestoes(data.sugestoes || []);
      if ((data.sugestoes || []).length === 0) {
        showSuccess('A IA não encontrou tags novas pra sugerir.');
      }
    } catch (err) {
      const status = err.response?.status;
      // #62 robustez 2: mensagem distinta por tipo de falha
      if (status === 504) {
        showError('A IA demorou mais que o esperado. Tente novamente em instantes.');
      } else if (status === 502) {
        showError('A IA está indisponível no momento. Tente novamente em instantes.');
      } else {
        showError(`Erro ao sugerir tags: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setSugerindo(false);
    }
  };

  const handleAceitarSugestao = async (sug) => {
    const chave = `${sug.tipo}:${sug.nome_normalizado}`;
    if (aceitando) return;
    setAceitando(chave);
    try {
      await ocorrenciaService.adicionarTag(ocId, {
        tipo: sug.tipo,
        nome: sug.nome,
        fonte: 'ia_sugerida_confirmada',
      });
      // Remove a sugestão aceita da lista
      setSugestoes((prev) =>
        (prev || []).filter(
          (s) => !(s.tipo === sug.tipo && s.nome_normalizado === sug.nome_normalizado)
        )
      );
      showSuccess('Tag confirmada.');
      await carregarTags();
    } catch (err) {
      showError(`Erro ao confirmar tag: ${err.response?.data?.detail || err.message}`);
    } finally {
      setAceitando(null);
    }
  };

  // #208 — aceitar todas as sugestões pendentes de uma vez (sequencial pra
  // manter as mensagens de erro por tag; volume típico é 3-6 sugestões).
  const handleConfirmarTodas = async () => {
    if (aceitando || confirmandoTodas) return;
    const pendentes = (sugestoes || []).filter((s) => !s.ja_associada);
    if (pendentes.length === 0) return;
    setConfirmandoTodas(true);
    let ok = 0;
    try {
      for (const sug of pendentes) {
        await ocorrenciaService.adicionarTag(ocId, {
          tipo: sug.tipo,
          nome: sug.nome,
          fonte: 'ia_sugerida_confirmada',
        });
        ok += 1;
      }
      setSugestoes([]);
      showSuccess(`${ok} ${ok === 1 ? 'tag confirmada' : 'tags confirmadas'}.`);
    } catch (err) {
      showError(`Erro ao confirmar (${ok}/${pendentes.length} ok): ${err.response?.data?.detail || err.message}`);
    } finally {
      setConfirmandoTodas(false);
      await carregarTags();
    }
  };

  const handleDescartarSugestao = (sug) => {
    setSugestoes((prev) =>
      (prev || []).filter(
        (s) => !(s.tipo === sug.tipo && s.nome_normalizado === sug.nome_normalizado)
      )
    );
  };

  return (
    <section className="oc-detail-section oc-tags-section">
      <h3>
        <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>sell</span>
        Tags de categorização
      </h3>
      <p className="oc-help" style={{ marginTop: 0 }}>
        Agrupam ocorrências semelhantes (processo, equipamento, turno…) pra análise
        de recorrência. Use a IA pra sugerir automaticamente ou adicione manualmente.
      </p>

      {/* Tags atuais */}
      {loading ? (
        <p className="oc-loading">Carregando tags…</p>
      ) : tags.length === 0 ? (
        <p className="oc-tags-vazio">Nenhuma tag ainda.</p>
      ) : (
        <div className="oc-tags-chips">
          {tags.map((t) => {
            const info = FONTE_INFO[t.fonte] || { icon: '', titulo: t.fonte };
            return (
              <span key={t.id} className="oc-tag-chip" title={info.titulo}>
                <span className="oc-tag-chip-tipo">{TIPO_LABEL[t.tipo] || t.tipo}</span>
                <span className="oc-tag-chip-nome">{t.nome}</span>
                <span className="oc-tag-chip-fonte" aria-hidden="true">
                  {info.icon && (
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>{info.icon}</span>
                  )}
                </span>
                {!locked && (
                  <button
                    type="button"
                    className="oc-tag-chip-remove"
                    onClick={() => handleRemover(t.id)}
                    title="Remover tag"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>close</span>
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {!locked && (
        <>
          {/* Ações: sugerir IA + adicionar manual */}
          <div className="oc-tags-actions">
            <button
              type="button"
              className="oc-btn-ia-sugerir"
              onClick={handleSugerir}
              disabled={sugerindo}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }}>smart_toy</span>
              {sugerindo ? 'Analisando…' : 'Sugerir com IA'}
            </button>
          </div>

          {/* Painel de sugestões */}
          {sugestoes !== null && sugestoes.length > 0 && (
            <div className="oc-tags-sugestoes">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0 }}>Sugestões da IA</h4>
                {/* #208 — atalho de aceite em lote */}
                {sugestoes.filter((s) => !s.ja_associada).length > 1 && (
                  <button
                    type="button"
                    className="oc-btn-aceitar-sug"
                    onClick={handleConfirmarTodas}
                    disabled={confirmandoTodas || !!aceitando}
                  >
                    {confirmandoTodas ? 'Confirmando…' : (
                      <>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '2px' }}>done_all</span>
                        Confirmar todas
                      </>
                    )}
                  </button>
                )}
              </div>
              <p className="oc-help" style={{ marginTop: 4, marginBottom: 8 }}>
                Confirme as que fizerem sentido. As demais são descartadas.
              </p>
              {sugestoes.map((sug) => {
                const chave = `${sug.tipo}:${sug.nome_normalizado}`;
                const pct = Math.round((sug.confianca || 0) * 100);
                return (
                  <div key={chave} className="oc-sugestao-item">
                    <div className="oc-sugestao-info">
                      <span className="oc-tag-chip-tipo">{TIPO_LABEL[sug.tipo] || sug.tipo}</span>
                      <span className="oc-sugestao-nome">{sug.nome}</span>
                      <span className="oc-sugestao-confianca" title="Confiança da IA">
                        {pct}%
                      </span>
                      {sug.ja_associada && (
                        <span className="oc-sugestao-ja" title="Já associada a esta ocorrência">
                          já adicionada
                        </span>
                      )}
                    </div>
                    <div className="oc-sugestao-botoes">
                      <button
                        type="button"
                        className="oc-btn-aceitar-sug"
                        onClick={() => handleAceitarSugestao(sug)}
                        disabled={aceitando === chave || sug.ja_associada}
                      >
                        {aceitando === chave ? '…' : (
                          <>
                            <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '2px' }}>check</span>
                            Confirmar
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="oc-btn-descartar-sug"
                        onClick={() => handleDescartarSugestao(sug)}
                        disabled={aceitando === chave}
                      >
                        Descartar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Form de adição manual */}
          <form className="oc-tags-add-form" onSubmit={handleAdicionarManual}>
            <select
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value)}
              disabled={adicionando}
              aria-label="Tipo da tag"
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              placeholder="Nome da tag (ex: extrusão)"
              maxLength={120}
              disabled={adicionando}
            />
            <button type="submit" className="oc-btn-add-tag" disabled={adicionando || !novoNome.trim()}>
              {adicionando ? '…' : '+ Adicionar'}
            </button>
          </form>
        </>
      )}
    </section>
  );
}

export default OcorrenciaTags;
